// Tier limits and feature gates. Single source of truth — used by useSubscription
// and by upgrade prompts everywhere in the app.

export type PlanTier = "personal" | "team" | "organization";

export interface TierLimits {
  maxTemplates: number;          // Infinity for unlimited
  maxContributorsPerCycle: number;
  maxCyclesPerMonth: number;
  customBranding: boolean;
  recurringSchedules: boolean;
  analytics: boolean;
  executiveRollups: boolean;
  advancedAi: boolean;             // polish, regenerate, custom voice
  exportFormats: ("pdf" | "pptx" | "docx")[];
  executiveViewers: number;
  managerSeats: number;
}

export const TIER_LIMITS: Record<PlanTier, TierLimits> = {
  personal: {
    maxTemplates: 1,
    maxContributorsPerCycle: 5,
    maxCyclesPerMonth: 2,
    customBranding: false,
    recurringSchedules: false,
    analytics: false,
    executiveRollups: false,
    advancedAi: false,
    exportFormats: ["pdf"],
    executiveViewers: 0,
    managerSeats: 1,
  },
  team: {
    maxTemplates: Infinity,
    maxContributorsPerCycle: 25,
    maxCyclesPerMonth: Infinity,
    customBranding: true,
    recurringSchedules: true,
    analytics: true,
    executiveRollups: false,
    advancedAi: true,
    exportFormats: ["pdf", "pptx", "docx"],
    executiveViewers: 2,
    managerSeats: 1,
  },
  organization: {
    maxTemplates: Infinity,
    maxContributorsPerCycle: Infinity,
    maxCyclesPerMonth: Infinity,
    customBranding: true,
    recurringSchedules: true,
    analytics: true,
    executiveRollups: true,
    advancedAi: true,
    exportFormats: ["pdf", "pptx", "docx"],
    executiveViewers: Infinity,
    managerSeats: 5,
  },
};

export const TIER_LABELS: Record<PlanTier, string> = {
  personal: "Personal",
  team: "Team",
  organization: "Organization",
};

export const TIER_PRICES = {
  team_monthly: { tier: "team" as const, interval: "month", amount: 29 },
  team_yearly: { tier: "team" as const, interval: "year", amount: 290 },
  organization_monthly: { tier: "organization" as const, interval: "month", amount: 79 },
  organization_yearly: { tier: "organization" as const, interval: "year", amount: 790 },
};

export function tierRank(tier: PlanTier): number {
  return tier === "personal" ? 0 : tier === "team" ? 1 : 2;
}

export function tierAtLeast(current: PlanTier, required: PlanTier): boolean {
  return tierRank(current) >= tierRank(required);
}
